// src/components/Admin/AnswerModal.jsx
import React, { useState } from 'react';
import { X, Send, Book, Sparkles } from 'lucide-react';

const AnswerModal = ({ question, onClose, onSubmit }) => {
  const [answer, setAnswer] = useState('');
  const [preview, setPreview] = useState('');
  const [scriptureRefs, setScriptureRefs] = useState([]);
  const [newRef, setNewRef] = useState('');
  const [aiAssisted, setAiAssisted] = useState(false);
  const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
        await onSubmit({
        answer,                // matches backend: AdminAnswerRequest.answer
        answerPreview: preview // matches backend: AdminAnswerRequest.answerPreview

        // For now we are NOT sending scriptureRefs / aiAssisted
        // until backend DTO and AskShiaAnswer entity are updated.
        // scriptureRefs,
        // aiAssisted,
        });
        onClose();
    } catch (error) {
        console.error('Error submitting answer:', error);
    } finally {
        setLoading(false);
    }
    };


  const addScriptureRef = () => {
    if (newRef.trim()) {
      setScriptureRefs([...scriptureRefs, newRef.trim()]);
      setNewRef('');
    }
  };

  const removeScriptureRef = (index) => {
    setScriptureRefs(scriptureRefs.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Answer Question</h3>
                <p className="text-sm text-gray-500 mt-1">Provide scriptural guidance for this seeker</p>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Question Preview */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">{question.title}</h4>
              <p className="text-gray-600">{question.body}</p>
              <div className="mt-3 text-sm text-gray-500">
                Asked by {question.user?.name || 'Anonymous'} • {new Date(question.createdAt).toLocaleDateString()}
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Answer Preview */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Answer Preview (Short Summary)
                </label>
                <textarea
                  value={preview}
                  onChange={(e) => setPreview(e.target.value)}
                  rows={2}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Brief summary that will appear in question lists..."
                  required
                />
              </div>

              {/* Full Answer */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Answer with Scriptural Guidance
                </label>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows={8}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Provide comprehensive guidance with scriptural references..."
                  required
                />
              </div>

              {/* Scripture References */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scripture References
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newRef}
                    onChange={(e) => setNewRef(e.target.value)}
                    placeholder="e.g., Bhagavad Gita 2.47"
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addScriptureRef())}
                  />
                  <button
                    type="button"
                    onClick={addScriptureRef}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Book className="h-4 w-4 mr-2" />
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {scriptureRefs.map((ref, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {ref}
                      <button
                        type="button"
                        onClick={() => removeScriptureRef(index)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* AI Assisted Toggle */}
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={aiAssisted}
                    onChange={(e) => setAiAssisted(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 flex items-center">
                    <Sparkles className="h-4 w-4 mr-1" />
                    AI-assisted answer (Gemini integration)
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {loading ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Answer
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnswerModal;